import {
  ChangeDetectorRef,
  HostBinding,
  Injectable,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { merge, Subject } from 'rxjs';
import { FormAlignmentsEnum } from '../../form-master.enum';
import { IFormFieldErrorMessages } from '../../form-master.interface';
import { filter, takeUntil } from 'rxjs/operators';

// should only be used as a superclass; this class is not injectable
@Injectable()
export class FormFieldComponent implements OnInit, OnDestroy {
  @HostBinding('attr.data-alignment')
  get componentAlignment(): FormAlignmentsEnum {
    return this.alignment || FormAlignmentsEnum.left;
  }

  protected destroyed: Subject<boolean> = new Subject<boolean>();
  protected errorMessages: IFormFieldErrorMessages;

  alignment: FormAlignmentsEnum;
  fieldControl: AbstractControl;

  constructor(
    protected changeDetectorRef: ChangeDetectorRef,
    protected _translateService: TranslateService
  ) {}

  // eslint-disable-next-line @angular-eslint/contextual-lifecycle
  ngOnInit() {
    if (this.fieldControl) {
      merge(this.fieldControl.valueChanges, this.fieldControl.statusChanges)
        .pipe(
          takeUntil(this.destroyed),
          filter(() => this.fieldControl.pristine)
        )
        .subscribe(() => this.changeDetectorRef.markForCheck());

      (this.fieldControl as FormControl).registerOnChange(() =>
        this.changeDetectorRef.markForCheck()
      );

      // overwrite markAsDirty fn to trigger change detection (to trigger error message visibility)
      const markAsDirtyFn = this.fieldControl.markAsDirty.bind(
        this.fieldControl
      );
      this.fieldControl.markAsDirty = () => {
        const result = markAsDirtyFn();

        this.changeDetectorRef.markForCheck();

        return result;
      };
    }
  }

  get errorMessage(): string {
    if (this.fieldControl && this.fieldControl.errors) {
      return this.getTranslatedErrorMessage(this.fieldControl.errors);
    }

    return '';
  }

  // XXX duplicated and mutated versions of this at:
  //   apps/invoice2go/src/app/modules/form-master/components/form-field/form-field.component.ts
  //   apps/client-portal/src/form-master/components/form-field/form-field.component.ts
  protected getTranslatedErrorMessage(errors: ValidationErrors): string {
    errors = errors || {};

    const errorMessages = this.errorMessages || {};
    let key: string = null;
    let params = {};

    const attrs: string[] = [
      'required',
      'maxlength',
      'minlength',
      'digits',
      'number',
      'range',
      'email',
      'pattern',
      'min',
      'max',
      'duplicatedRate',
    ];

    for (const attr of attrs) {
      if (errors[attr]) {
        let modified_attr = attr.toUpperCase();
        if (attr == 'maxlength') {
          modified_attr = 'MAX_LENGTH';
        } else if (attr == 'minlength') {
          modified_attr = 'MIN_LENGTH';
        } else if (attr == 'duplicatedRate') {
          modified_attr = 'DUPLICATED_RATE';
        }

        let default_params = {};
        if (attr == 'maxlength') {
          default_params = {
            maxLength: errors[attr].requiredLength, // note camelCase here
          };
        } else if (attr == 'minlength') {
          default_params = {
            minLength: errors[attr].requiredLength, // note camelCase here
          };
        } else if (attr == 'min' || attr == 'max') {
          default_params = {
            [attr]: errors[attr][attr],
          };
        }

        key = errorMessages[attr]?.message || ('ERROR.FIELD_' + modified_attr);
        params = errorMessages[attr]?.translateParams || default_params;
        break;
      }
    }

    if (key === null) {
      // adding a custom rule here so we can pass in rules on the fly
      const errorKey = Object.keys(errors)[0];

      if (!errorKey) {
        // This takes care of the situation where
        // dev hasn't provided error message
        // so we supply a generic one
        key = errorMessages.generic?.message || 'ERROR.FIELD_INVALID';
        params = {};
      } else {
        // The second part is required due to angular material date picker
        key =
          errors[errorKey]?.message ||
          errorMessages[errorKey]?.message ||
          errors[errorKey]?.text;
        params =
          errors[errorKey]?.translateParams ||
          errorMessages[errorKey]?.translateParams;
      }
    }

    if (key && key.length > 0) {
      return this._translateService.instant(key, params);
    }

    return '';
  }

  ngOnDestroy() {
    this.destroyed.next(true);
    this.destroyed.unsubscribe();
  }
}
